import { useState, useCallback, useEffect, useRef } from 'react';
import type {
  WorkplaceSovStep,
  WorkplaceSovState,
  InfrastructureMode,
  DeploymentModel,
  IstSelection,
  SovWeights,
  OperationsAnswers,
} from '../types/workplace';
import { DEFAULT_SOV_WEIGHTS } from '../utils/sovScoreCalculations';

const STORAGE_KEY = 'sovereign-workplace-sov-state';

const INITIAL_OPERATIONS_ANSWERS: OperationsAnswers = { q1: null, q2: null, q3: null, q4: null };

const INITIAL_STATE: WorkplaceSovState = {
  currentStep: 'infrastructure',
  infrastructureMode: null,
  cloudProvider: null,
  istSelections: {},
  zielSelections: {},
  istOperationsAnswers: { ...INITIAL_OPERATIONS_ANSWERS },
  zielOperationsAnswers: { ...INITIAL_OPERATIONS_ANSWERS },
  sovWeights: { ...DEFAULT_SOV_WEIGHTS },
};

// --- v1 → v2 State Migration Maps ---

const PROVIDER_RENAME_MAP: Record<string, string> = {
  'Microsoft': 'Microsoft 365',
  'Fedora': 'Fedora / RHEL',
  'X-Wiki': 'XWiki',
};

const CATEGORY_RENAME_MAP: Record<string, string> = {
  'Other: Open Source': 'Other: EU Open Source',
  'Other: EU Provider': 'Other: EU Provider (Proprietary)',
  'Other: Non-EU Provider': 'Other: Non-EU Provider (Proprietary)',
};

const REMOVED_PROVIDERS = new Set([
  'Apache Open Office', 'OSSEC', 'OpenDLP', 'Puppet',
]);

const DEPLOYMENT_RENAME_MAP: Record<string, string> = {
  'managed-eu': 'hosted-eu',
};

/**
 * Migrate persisted v1 state to v2 format:
 * - Rename deployments (managed-eu → hosted-eu)
 * - Rename providers (Microsoft → Microsoft 365, etc.)
 * - Map old generic categories to new names
 * - Remove selections for deleted providers
 * On any error, resets to INITIAL_STATE and clears localStorage.
 */
export function migrateState(parsed: WorkplaceSovState): WorkplaceSovState {
  try {
    const migrateSelections = (
      selections: Record<string, IstSelection>,
    ): Record<string, IstSelection> => {
      const migrated: Record<string, IstSelection> = {};
      for (const [key, sel] of Object.entries(selections)) {
        // Apply provider rename
        let provider = PROVIDER_RENAME_MAP[sel.provider] ?? sel.provider;
        // Apply category rename
        provider = CATEGORY_RENAME_MAP[provider] ?? provider;
        // Skip removed providers
        if (REMOVED_PROVIDERS.has(provider)) continue;
        // Apply deployment rename
        const deployment = (DEPLOYMENT_RENAME_MAP[sel.deployment] ?? sel.deployment) as DeploymentModel;
        migrated[key] = { provider, deployment };
      }
      return migrated;
    };

    return {
      ...parsed,
      istSelections: migrateSelections(parsed.istSelections),
      zielSelections: migrateSelections(parsed.zielSelections),
      istOperationsAnswers: parsed.istOperationsAnswers ?? { ...INITIAL_OPERATIONS_ANSWERS },
      zielOperationsAnswers: parsed.zielOperationsAnswers ?? { ...INITIAL_OPERATIONS_ANSWERS },
    };
  } catch {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return { ...INITIAL_STATE };
  }
}

function loadState(): WorkplaceSovState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed !== 'object' || parsed === null ||
      typeof parsed.currentStep !== 'string' ||
      typeof parsed.istSelections !== 'object' ||
      typeof parsed.zielSelections !== 'object' ||
      typeof parsed.sovWeights !== 'object'
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return migrateState(parsed as WorkplaceSovState);
  } catch {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return null;
  }
}

function saveState(state: WorkplaceSovState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota exceeded or private browsing — silently ignore */ }
}

export function useWorkplaceSovState() {
  const [currentStep, setCurrentStepRaw] = useState<WorkplaceSovStep>(INITIAL_STATE.currentStep);
  const [infrastructureMode, setInfrastructureModeRaw] = useState<InfrastructureMode | null>(INITIAL_STATE.infrastructureMode);
  const [cloudProvider, setCloudProviderRaw] = useState<string | null>(INITIAL_STATE.cloudProvider);
  const [istSelections, setIstSelectionsRaw] = useState<Record<string, IstSelection>>(INITIAL_STATE.istSelections);
  const [zielSelections, setZielSelectionsRaw] = useState<Record<string, IstSelection>>(INITIAL_STATE.zielSelections);
  const [istOperationsAnswers, setIstOperationsAnswersRaw] = useState<OperationsAnswers>(INITIAL_STATE.istOperationsAnswers);
  const [zielOperationsAnswers, setZielOperationsAnswersRaw] = useState<OperationsAnswers>(INITIAL_STATE.zielOperationsAnswers);
  const [sovWeights, setSovWeightsRaw] = useState<SovWeights>(INITIAL_STATE.sovWeights);
  const [hasSavedState, setHasSavedState] = useState(false);
  const initialized = useRef(false);

  // On mount: check for saved state
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const saved = loadState();
    setHasSavedState(saved !== null);
  }, []);

  // Persist on every state change (skip initial render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    saveState({ currentStep, infrastructureMode, cloudProvider, istSelections, zielSelections, istOperationsAnswers, zielOperationsAnswers, sovWeights });
  }, [currentStep, infrastructureMode, cloudProvider, istSelections, zielSelections, istOperationsAnswers, zielOperationsAnswers, sovWeights]);

  // --- Actions ---
  const setCurrentStep = useCallback((step: WorkplaceSovStep) => setCurrentStepRaw(step), []);

  const setInfrastructureMode = useCallback((mode: InfrastructureMode) => setInfrastructureModeRaw(mode), []);

  const setCloudProvider = useCallback((provider: string) => setCloudProviderRaw(provider), []);

  const setIstSelection = useCallback((featureGroupId: string, provider: string | null, deployment?: DeploymentModel) => {
    setIstSelectionsRaw(prev => {
      if (provider === null) {
        const next = { ...prev };
        delete next[featureGroupId];
        return next;
      }
      return { ...prev, [featureGroupId]: { provider, deployment: deployment ?? 'saas' } };
    });
  }, []);

  const setZielSelection = useCallback((featureGroupId: string, provider: string | null, deployment?: DeploymentModel) => {
    setZielSelectionsRaw(prev => {
      if (provider === null) {
        const next = { ...prev };
        delete next[featureGroupId];
        return next;
      }
      return { ...prev, [featureGroupId]: { provider, deployment: deployment ?? 'saas' } };
    });
  }, []);

  const setSovWeight = useCallback((sovId: string, value: number) => {
    setSovWeightsRaw(prev => ({ ...prev, [sovId]: value }));
  }, []);

  const resetWeights = useCallback(() => setSovWeightsRaw({ ...DEFAULT_SOV_WEIGHTS }), []);

  const initZielFromIst = useCallback(() => {
    setZielSelectionsRaw(prev => {
      // Deep copy istSelections
      const copy: Record<string, IstSelection> = {};
      for (const [key, val] of Object.entries(istSelections)) {
        copy[key] = { ...val };
      }
      return copy;
    });
  }, [istSelections]);

  const setOperationsAnswer = useCallback((context: 'ist' | 'ziel', questionId: 'q1' | 'q2' | 'q3' | 'q4', sealValue: number) => {
    if (context === 'ist') {
      setIstOperationsAnswersRaw(prev => ({ ...prev, [questionId]: sealValue }));
    } else {
      setZielOperationsAnswersRaw(prev => ({ ...prev, [questionId]: sealValue }));
    }
  }, []);

  const initZielOperationsFromIst = useCallback(() => {
    setZielOperationsAnswersRaw({ ...istOperationsAnswers });
  }, [istOperationsAnswers]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setCurrentStepRaw(INITIAL_STATE.currentStep);
    setInfrastructureModeRaw(INITIAL_STATE.infrastructureMode);
    setCloudProviderRaw(INITIAL_STATE.cloudProvider);
    setIstSelectionsRaw(INITIAL_STATE.istSelections);
    setZielSelectionsRaw(INITIAL_STATE.zielSelections);
    setIstOperationsAnswersRaw({ ...INITIAL_OPERATIONS_ANSWERS });
    setZielOperationsAnswersRaw({ ...INITIAL_OPERATIONS_ANSWERS });
    setSovWeightsRaw({ ...INITIAL_STATE.sovWeights });
    setHasSavedState(false);
  }, []);

  const continueSavedState = useCallback(() => {
    const saved = loadState();
    if (!saved) return;
    setCurrentStepRaw(saved.currentStep);
    setInfrastructureModeRaw(saved.infrastructureMode);
    setCloudProviderRaw(saved.cloudProvider);
    setIstSelectionsRaw(saved.istSelections);
    setZielSelectionsRaw(saved.zielSelections);
    setIstOperationsAnswersRaw(saved.istOperationsAnswers);
    setZielOperationsAnswersRaw(saved.zielOperationsAnswers);
    setSovWeightsRaw(saved.sovWeights);
    setHasSavedState(false);
  }, []);

  return {
    currentStep,
    infrastructureMode,
    cloudProvider,
    istSelections,
    zielSelections,
    istOperationsAnswers,
    zielOperationsAnswers,
    sovWeights,
    hasSavedState,
    setCurrentStep,
    setInfrastructureMode,
    setCloudProvider,
    setIstSelection,
    setZielSelection,
    setOperationsAnswer,
    initZielOperationsFromIst,
    setSovWeight,
    resetWeights,
    initZielFromIst,
    reset,
    continueSavedState,
  };
}
